import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Camera,
  Download,
  FileSpreadsheet,
  History,
  Info,
  RotateCcw,
  Upload,
} from "lucide-react";
import { ADMIN_TOKEN_KEY, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const sectionClass = "bg-card border border-border rounded-lg p-5 space-y-4";

type Finding = {
  severity: "error" | "warning";
  code: string;
  message: string;
  samples?: string[];
  count?: number;
};

type FieldChange = { field: string; from: unknown; to: unknown };
type RowDiff = { sku: string; name: string; changes: FieldChange[] };

type ImportPreview = {
  totalRows: number;
  matched: number;
  unmatched: number;
  unchanged: number;
  productsChanged: number;
  fieldCounts: Record<string, number>;
  findings: Finding[];
  sampleChanges: RowDiff[];
  missingColumns: string[];
};

type Snapshot = {
  id: string;
  created_at: string;
  reason: "daily" | "manual" | "import";
  label: string | null;
  product_count: number;
};

const FIELD_LABELS: Record<string, string> = {
  name: "Nosaukums",
  vintage: "Gads",
  barcode: "Svītrukods",
  sale_price: "Akcijas cena",
  region: "Reģions",
  country: "Valsts",
  volume_ml: "Tilpums",
  abv: "Alkohols",
  serve_temp: "Pasniegšanas temp.",
  type_scale: "Saldums",
  strength_scale: "Uzbūve",
  active: "Publicēts",
  featured: "Priekšplānā",
  price_base: "Cena",
  price_special_a: "Cena Special A",
  price_special_b: "Cena Special B",
  description: "Pilnais apraksts",
  shortDescription: "Īss apraksts",
  measure_type: "Mērvienība",
  weight_g: "Svars (g)",
  measure_display: "Attēlojums",
  metaTitle: "SEO nosaukums",
  metaDescription: "SEO apraksts",
  translations: "Teksti",
};
const label = (f: string) => FIELD_LABELS[f] ?? f;

const fmtValue = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

export function CatalogSettings() {
  const { toast } = useToast();
  const t = useT();

  const importInputRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);

  // Import is a two-step flow: upload → preview → approve. `phase` drives both
  // the progress bar and which dialog is on screen.
  const [phase, setPhase] = useState<"idle" | "uploading" | "analysing" | "applying">("idle");
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importToken, setImportToken] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");

  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(true);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<Snapshot | null>(null);
  const [snapshotting, setSnapshotting] = useState(false);
  // Overriding blocking findings is deliberate: the user types the confirmation
  // word rather than clicking one more button, so it can't be done on autopilot.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideText, setOverrideText] = useState("");

  const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:3004";

  const loadSnapshots = useCallback(async () => {
    setSnapshotsLoading(true);
    try {
      const res = await api<{ snapshots: Snapshot[] }>("/api/v1/admin/snapshots");
      setSnapshots(res.snapshots ?? []);
    } catch {
      // Non-fatal: the import/export tools still work without the list.
    } finally {
      setSnapshotsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSnapshots();
  }, [loadSnapshots]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem(ADMIN_TOKEN_KEY);
      const res = await fetch(`${API_BASE}/api/v1/admin/catalog/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `product_catalog_${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: t("catalogSettings.exportFailed", "Eksports neizdevās"),
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  /** Step 1: upload the sheet and get back a diff, without writing anything. */
  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setPhase("uploading");
    setProgress(0);

    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const fd = new FormData();
    fd.append("file", file);

    // XHR rather than fetch: it reports real upload progress, which is the
    // whole point of this change — a 600KB sheet over a slow connection used
    // to sit on "Importē…" with no indication of anything happening.
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/api/v1/admin/catalog/import/preview`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable) return;
      // Upload is the first 60% of the bar; the server-side analysis is the rest.
      setProgress(Math.round((ev.loaded / ev.total) * 60));
    };
    xhr.upload.onload = () => {
      setPhase("analysing");
      setProgress(65);
    };

    xhr.onload = () => {
      setProgress(100);
      try {
        const data = JSON.parse(xhr.responseText) as
          | { token: string; preview: ImportPreview }
          | { error: string };
        if (xhr.status >= 400 || "error" in data) {
          toast({
            title: t("catalogSettings.importFailed", "Imports neizdevās"),
            description: "error" in data ? data.error : undefined,
            variant: "destructive",
          });
          setPhase("idle");
          return;
        }
        setImportToken(data.token);
        setPreview(data.preview);
        setPhase("idle");
      } catch {
        toast({
          title: t("catalogSettings.importFailed", "Imports neizdevās"),
          variant: "destructive",
        });
        setPhase("idle");
      } finally {
        if (importInputRef.current) importInputRef.current.value = "";
      }
    };
    xhr.onerror = () => {
      toast({
        title: t("catalogSettings.importFailed", "Imports neizdevās"),
        variant: "destructive",
      });
      setPhase("idle");
      if (importInputRef.current) importInputRef.current.value = "";
    };

    xhr.send(fd);
  };

  /** Step 2: the user has seen the diff and pressed Approve. */
  const applyImport = async (acknowledge: string[] = []) => {
    if (!importToken) return;
    setOverrideOpen(false);
    setOverrideText("");
    setPhase("applying");
    setProgress(30);
    try {
      const res = await api<{
        updated: number;
        skipped: number;
        errors: unknown[];
        snapshotId?: string;
        overrode?: boolean;
      }>("/api/v1/admin/catalog/import/apply", {
        method: "POST",
        body: JSON.stringify({ token: importToken, acknowledge }),
      });
      setProgress(100);
      toast({
        title:
          t("catalogSettings.importDone", "Imports pabeigts") +
          ` — ${t("catalogSettings.updated", "atjaunināti")} ${res.updated}, ` +
          `${t("catalogSettings.skipped", "izlaisti")} ${res.skipped}`,
        description:
          (res.overrode
            ? t(
                "catalogSettings.overrodeNote",
                "Brīdinājumi tika ignorēti. ",
              )
            : "") +
          (res.snapshotId
            ? t(
                "catalogSettings.snapshotTaken",
                "Pirms importa izveidota momentuzņēmuma kopija — vajadzības gadījumā varat atgriezties.",
              )
            : ""),
      });
      setPreview(null);
      setImportToken(null);
      void loadSnapshots();
    } catch (err) {
      toast({
        title: t("catalogSettings.importFailed", "Imports neizdevās"),
        description: String(err),
        variant: "destructive",
      });
    } finally {
      setPhase("idle");
    }
  };

  const takeSnapshot = async () => {
    setSnapshotting(true);
    try {
      await api("/api/v1/admin/snapshots", {
        method: "POST",
        body: JSON.stringify({ label: "Manuāls" }),
      });
      toast({ title: t("snapshots.created", "Momentuzņēmums izveidots") });
      void loadSnapshots();
    } catch {
      toast({
        title: t("snapshots.createFailed", "Neizdevās izveidot momentuzņēmumu"),
        variant: "destructive",
      });
    } finally {
      setSnapshotting(false);
    }
  };

  const doRestore = async (snap: Snapshot) => {
    setRestoring(snap.id);
    setConfirmRestore(null);
    try {
      const res = await api<{ restored: number; skippedMissing: number }>(
        `/api/v1/admin/snapshots/${snap.id}/restore`,
        { method: "POST" },
      );
      toast({
        title: t("snapshots.restored", "Atjaunots"),
        description:
          `${res.restored} ${t("snapshots.productsRestored", "preces atjaunotas")}` +
          (res.skippedMissing
            ? `, ${res.skippedMissing} ${t("snapshots.skippedMissing", "vairs neeksistē")}`
            : ""),
      });
      void loadSnapshots();
    } catch {
      toast({
        title: t("snapshots.restoreFailed", "Atjaunošana neizdevās"),
        variant: "destructive",
      });
    } finally {
      setRestoring(null);
    }
  };

  const busy = phase !== "idle";
  const blocking = (preview?.findings ?? []).filter((f) => f.severity === "error");
  const warnings = (preview?.findings ?? []).filter((f) => f.severity === "warning");

  const phaseLabel =
    phase === "uploading"
      ? t("catalogSettings.uploading", "Augšupielādē failu…")
      : phase === "analysing"
        ? t("catalogSettings.analysing", "Analizē izmaiņas…")
        : phase === "applying"
          ? t("catalogSettings.applying", "Piemēro izmaiņas…")
          : "";

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-bold text-foreground">
          {t("catalogSettings.title", "Kataloga imports / eksports")}
        </h1>
        <p className="font-body text-xs text-muted-foreground">
          {t(
            "catalogSettings.subtitle",
            "XLSX fails ar 6 lapām: Preces, Kategorijas, Valstis, Tipi, Regioni, Vinogu_Skirnes. Imports atjauno produktus pēc SKU (Kods); jauni produkti jāveido produktu redaktorā.",
          )}
        </p>
      </div>

      {/* EXPORT */}
      <section className={sectionClass}>
        <div className="flex items-start gap-3">
          <FileSpreadsheet size={18} className="mt-0.5 text-muted-foreground" />
          <div className="flex-1">
            <h2 className="font-display text-sm font-bold text-foreground">
              {t("catalogSettings.exportTitle", "Eksportēt katalogu")}
            </h2>
            <p className="font-body text-xs text-muted-foreground">
              {t(
                "catalogSettings.exportHint",
                "Lejupielādē visu produktu katalogu kā XLSX failu tādā pašā formātā kā vecajā admin sistēmā. Beigās pievienotas kolonnas, kurām vecajā formātā nav vietas: Cena_Special_A, Cena_Special_B (speciālo cenrāžu cenas), Iss_apraksts, Apraksts un Edienu_saderiba. Vienmēr rediģē tikai šo failu — vecā admina eksportā šo kolonnu nav, tāpēc izmaiņas tajās netiek saglabātas.",
              )}
            </p>
          </div>
          <Button onClick={handleExport} disabled={exporting}>
            <Download size={16} />
            {exporting
              ? t("catalogSettings.exporting", "Eksportē…")
              : t("catalogSettings.export", "Eksportēt")}
          </Button>
        </div>
      </section>

      {/* IMPORT */}
      <section className={sectionClass}>
        <div className="flex items-start gap-3">
          <FileSpreadsheet size={18} className="mt-0.5 text-muted-foreground" />
          <div className="flex-1">
            <h2 className="font-display text-sm font-bold text-foreground">
              {t("catalogSettings.importTitle", "Importēt katalogu")}
            </h2>
            <p className="font-body text-xs text-muted-foreground">
              {t(
                "catalogSettings.importHint",
                "Augšupielādē rediģētu XLSX failu. Vispirms tiks parādīts pārskats par to, kas mainīsies — nekas netiek saglabāts, kamēr neapstiprināt. Sistēma atjauno tikai esošos produktus pēc SKU (Kods). Cenu kolonnas: tukša šūna cenu nemaina, “-” dzēš speciālo cenu.",
              )}
            </p>
          </div>
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={handleFilePicked}
          />
          <Button
            variant="outline"
            onClick={() => importInputRef.current?.click()}
            disabled={busy}
          >
            <Upload size={16} />
            {busy
              ? t("catalogSettings.importing", "Importē…")
              : t("catalogSettings.import", "Izvēlēties failu")}
          </Button>
        </div>

        {busy && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="font-body text-xs text-muted-foreground">
                {phaseLabel} {fileName && <span className="opacity-70">({fileName})</span>}
              </span>
              <span className="font-mono text-xs text-muted-foreground">{progress}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}
      </section>

      {/* SNAPSHOTS */}
      <section className={sectionClass}>
        <div className="flex items-start gap-3">
          <History size={18} className="mt-0.5 text-muted-foreground" />
          <div className="flex-1">
            <h2 className="font-display text-sm font-bold text-foreground">
              {t("snapshots.title", "Momentuzņēmumi")}
            </h2>
            <p className="font-body text-xs text-muted-foreground">
              {t(
                "snapshots.hint",
                "Katalogs tiek automātiski saglabāts reizi dienā un pirms katra importa. Glabājas pēdējie 10. Atjaunošana atgriež produktu datus un cenas — attēli, krājumi un kategorijas netiek mainītas.",
              )}
            </p>
          </div>
          <Button variant="outline" onClick={takeSnapshot} disabled={snapshotting}>
            <Camera size={16} />
            {snapshotting
              ? t("snapshots.creating", "Saglabā…")
              : t("snapshots.create", "Saglabāt tagad")}
          </Button>
        </div>

        {snapshotsLoading ? (
          <p className="font-body text-xs text-muted-foreground">
            {t("common.loading", "Ielādē…")}
          </p>
        ) : snapshots.length === 0 ? (
          <p className="font-body text-xs text-muted-foreground">
            {t("snapshots.empty", "Vēl nav neviena momentuzņēmuma.")}
          </p>
        ) : (
          <div className="divide-y divide-border rounded-md border border-border">
            {snapshots.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-body text-sm text-foreground">
                      {new Date(s.created_at).toLocaleString("lv-LV", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <Badge variant={s.reason === "import" ? "outline" : "secondary"}>
                      {s.reason === "daily"
                        ? t("snapshots.daily", "Ikdienas")
                        : s.reason === "import"
                          ? t("snapshots.beforeImport", "Pirms importa")
                          : t("snapshots.manual", "Manuāls")}
                    </Badge>
                  </div>
                  <div className="truncate font-body text-xs text-muted-foreground">
                    {s.product_count} {t("snapshots.products", "preces")}
                    {s.label ? ` · ${s.label}` : ""}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={restoring !== null}
                  onClick={() => setConfirmRestore(s)}
                >
                  <RotateCcw size={14} />
                  {restoring === s.id
                    ? t("snapshots.restoring", "Atjauno…")
                    : t("snapshots.restore", "Atjaunot")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* PREVIEW / APPROVE */}
      <Dialog
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPreview(null);
            setImportToken(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("catalogSettings.previewTitle", "Pārskats pirms importa")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "catalogSettings.previewSubtitle",
                "Nekas vēl nav saglabāts. Pārbaudiet izmaiņas un apstipriniet.",
              )}
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="space-y-4">
              {/* Headline numbers */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  [t("catalogSettings.rowsInFile", "Rindas failā"), preview.totalRows],
                  [t("catalogSettings.willChange", "Mainīsies"), preview.productsChanged],
                  [t("catalogSettings.unchanged", "Bez izmaiņām"), preview.unchanged],
                  [t("catalogSettings.notFound", "Netika atrasti"), preview.unmatched],
                ].map(([k, v]) => (
                  <div key={String(k)} className="rounded-md border border-border p-2.5">
                    <div className="font-display text-lg font-bold text-foreground">{v}</div>
                    <div className="font-body text-[11px] text-muted-foreground">{k}</div>
                  </div>
                ))}
              </div>

              {/* Blocking problems */}
              {blocking.length > 0 && (
                <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                  <div className="flex items-center gap-2 font-display text-sm font-bold text-destructive">
                    <AlertTriangle size={15} />
                    {t("catalogSettings.seriousWarnings", "Nopietni brīdinājumi")}
                  </div>
                  <p className="font-body text-xs text-muted-foreground">
                    {t(
                      "catalogSettings.seriousWarningsHint",
                      "Šīs pārbaudes ir automātiskas un var kļūdīties. Ja esat pārliecināts, ka fails ir pareizs, importu var veikt arī tāpat.",
                    )}
                  </p>
                  {blocking.map((f, i) => (
                    <div key={i} className="font-body text-xs text-foreground">
                      • {f.message}
                      {f.samples?.length ? (
                        <div className="ml-3 font-mono text-[11px] text-muted-foreground">
                          {f.samples.join(", ")}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              {/* Non-blocking notes */}
              {warnings.length > 0 && (
                <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                  <div className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
                    <Info size={15} className="text-muted-foreground" />
                    {t("catalogSettings.notices", "Piezīmes")}
                  </div>
                  {warnings.map((f, i) => (
                    <div key={i} className="font-body text-xs text-muted-foreground">
                      • {f.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Which fields change, and how widely */}
              {Object.keys(preview.fieldCounts).length > 0 && (
                <div>
                  <h3 className="mb-1.5 font-display text-sm font-bold text-foreground">
                    {t("catalogSettings.fieldsChanging", "Lauki, kas mainīsies")}
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(preview.fieldCounts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([field, n]) => (
                        <span
                          key={field}
                          className={cn(
                            "rounded border px-2 py-1 font-body text-xs",
                            n / Math.max(preview.matched, 1) >= 0.6
                              ? "border-destructive/40 bg-destructive/5 text-destructive"
                              : "border-border text-muted-foreground",
                          )}
                        >
                          {label(field)} · {n}
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* A concrete sample so the numbers are checkable */}
              {preview.sampleChanges.length > 0 && (
                <div>
                  <h3 className="mb-1.5 font-display text-sm font-bold text-foreground">
                    {t("catalogSettings.sample", "Piemēri")}
                    <span className="ml-1.5 font-body text-xs font-normal text-muted-foreground">
                      ({Math.min(preview.sampleChanges.length, 15)}{" "}
                      {t("catalogSettings.ofChanged", "no")} {preview.productsChanged})
                    </span>
                  </h3>
                  <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-border p-2">
                    {preview.sampleChanges.slice(0, 15).map((r) => (
                      <div key={r.sku} className="font-body text-xs">
                        <div className="font-medium text-foreground">
                          <span className="font-mono text-muted-foreground">{r.sku}</span>{" "}
                          {r.name}
                        </div>
                        {r.changes.map((ch, i) => (
                          <div key={i} className="ml-3 text-muted-foreground">
                            {label(ch.field)}:{" "}
                            <span className="line-through opacity-60">
                              {fmtValue(ch.from)}
                            </span>{" "}
                            → <span className="text-foreground">{fmtValue(ch.to)}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPreview(null);
                setImportToken(null);
              }}
            >
              {t("common.cancel", "Atcelt")}
            </Button>
            {blocking.length > 0 ? (
              <Button
                variant="destructive"
                onClick={() => setOverrideOpen(true)}
                disabled={preview?.productsChanged === 0}
              >
                <AlertTriangle size={16} />
                {t("catalogSettings.importAnyway", "Tomēr importēt")}
              </Button>
            ) : (
              <Button
                onClick={() => applyImport()}
                disabled={preview?.productsChanged === 0}
              >
                <Check size={16} />
                {t("catalogSettings.approve", "Apstiprināt un importēt")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OVERRIDE CONFIRMATION — typing, not just one more click */}
      <Dialog
        open={overrideOpen}
        onOpenChange={(open) => {
          setOverrideOpen(open);
          if (!open) setOverrideText("");
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {t("catalogSettings.overrideTitle", "Importēt, neskatoties uz brīdinājumiem?")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "catalogSettings.overrideBody",
                "Pārbaudes atrada izmaiņas, kas parasti norāda uz bojātu failu. Turpiniet tikai tad, ja esat pārliecināts, ka fails ir pareizs.",
              )}
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3">
            {blocking.map((f, i) => (
              <li key={i} className="font-body text-xs text-foreground">
                • {f.message}
              </li>
            ))}
          </ul>

          <p className="font-body text-xs text-muted-foreground">
            {t(
              "catalogSettings.overrideSafety",
              "Pirms importa tiks saglabāta pašreizējā versija, tāpēc izmaiņas varēs atsaukt sadaļā Momentuzņēmumi.",
            )}
          </p>

          <div className="space-y-1.5">
            <label className="font-body text-xs text-muted-foreground">
              {t("catalogSettings.overrideTypePrompt", "Lai turpinātu, ierakstiet")}{" "}
              <span className="font-mono font-bold text-foreground">IMPORTĒT</span>
            </label>
            <input
              value={overrideText}
              onChange={(e) => setOverrideText(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-lg border border-border bg-card px-3 py-2 font-mono text-sm text-foreground outline-none focus:border-destructive"
              placeholder="IMPORTĒT"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideOpen(false)}>
              {t("common.cancel", "Atcelt")}
            </Button>
            <Button
              variant="destructive"
              disabled={overrideText.trim().toUpperCase() !== "IMPORTĒT"}
              onClick={() => applyImport([...new Set(blocking.map((f) => f.code))])}
            >
              <AlertTriangle size={16} />
              {t("catalogSettings.overrideConfirm", "Importēt tāpat")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RESTORE CONFIRMATION */}
      <AlertDialog
        open={confirmRestore !== null}
        onOpenChange={(open) => !open && setConfirmRestore(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("snapshots.confirmTitle", "Atjaunot šo momentuzņēmumu?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "snapshots.confirmBody",
                "Produktu dati un cenas tiks atgrieztas uz stāvokli",
              )}{" "}
              {confirmRestore &&
                new Date(confirmRestore.created_at).toLocaleString("lv-LV")}
              .{" "}
              {t(
                "snapshots.confirmBody2",
                "Pirms atjaunošanas tiks saglabāta pašreizējā versija, tāpēc šo darbību var atsaukt.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Atcelt")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRestore && doRestore(confirmRestore)}
            >
              {t("snapshots.restore", "Atjaunot")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
